import { defineNuxtPlugin, useRuntimeConfig, useRoute } from '#imports'

interface UserResponse {
  user: {
    id: number;
    profile?: {
      language?: string;  // Can be locale format like "fr-CA"
    }
  } | null;
}

declare global {
  interface Window {
    Localize?: {
      translate: () => void;
      untranslate: () => void;
      phrase: () => void;
      initialize: (config: { key: string; rememberLanguage: boolean }) => void;
      translatePage: () => void;
      setLanguage: (language: string) => void;
      getLanguage: () => void;
      getSourceLanguage: () => void;
      detectLanguage: () => void;
      getAvailableLanguages: () => void;
      untranslatePage: () => void;
      bootstrap: () => void;
      prefetch: () => void;
      on: () => void;
      off: () => void;
      hideWidget: () => void;
      showWidget: () => void;
    };
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  // Only run on client-side
  if (process.server) return

  const config = useRuntimeConfig()
  const route = useRoute()
  const localizeKey = config.public.localizeKey

  // Don't proceed if Localize key isn't configured
  if (!localizeKey) return

  // Check if user is authenticated first
  try {
    const { data } = await useFetch<UserResponse>(`/api/${route.params.org_slug}/auth`, {
      headers: {
        Accept: 'application/json',
        'X-Api-Request': 'true'
      }
    })

    // Only proceed if we have authenticated user
    if (!data.value?.user) return

    // Initialize empty Localize object if it doesn't exist
    if (!window.Localize) {
      window.Localize = {} as any;
      const methods = [
        "translate",
        "untranslate",
        "phrase",
        "initialize",
        "translatePage",
        "setLanguage",
        "getLanguage",
        "getSourceLanguage",
        "detectLanguage",
        "getAvailableLanguages",
        "untranslatePage",
        "bootstrap",
        "prefetch",
        "on",
        "off",
        "hideWidget",
        "showWidget"
      ];

      methods.forEach(method => {
        window.Localize![method as keyof typeof window.Localize] = function() {};
      });
    }

    // Load Localize script
    const script = document.createElement('script')
    script.src = 'https://global.localizecdn.com/localize.473.js'

    script.onload = () => {
      // Initialize Localize
      window.Localize?.initialize({
        key: localizeKey,
        rememberLanguage: true
      })

      // Set language if user has a preferred language
      const userLanguage = data.value.user.profile?.language
      if (userLanguage) {
        // Localize accepts both formats:
        // - Two-letter codes: 'en', 'pt'
        // - Locale codes: 'fr-CA'
        window.Localize?.setLanguage(userLanguage)
        console.debug(`Setting Localize language to: ${userLanguage}`)
      } else {
        console.debug('No language preference found in user profile')
      }
    }

    document.head.appendChild(script)

  } catch (error) {
    console.error('Failed to initialize Localize:', error)
  }
})